import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { ShareChromeProvider, useShareChrome } from './contexts/ShareChromeContext';
import { Layout, Spin, Grid } from 'antd';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import HeaderBar from './components/HeaderBar';
import NotificationContainer from './components/NotificationContainer';
import { NotificationProvider } from './contexts/NotificationContext';
import { SearchProvider } from './contexts/SearchContext';
import { UserSessionProvider } from './contexts/UserSessionContext';
import { CollectionsDataProvider } from './contexts/CollectionsDataContext';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import DashboardOverview from './pages/dashboard/DashboardOverview';
import DashboardUsage from './pages/dashboard/DashboardUsage';
import DashboardReliability from './pages/dashboard/DashboardReliability';
import DashboardResponseQuality from './pages/dashboard/DashboardResponseQuality';
import DashboardSpend from './pages/dashboard/DashboardSpend';
import UserManagement from './pages/UserManagement';
import CreateUser from './pages/CreateUser';
import CreateGroup from './pages/CreateGroup';
import UserProfile from './pages/UserProfile';
import Chat from './pages/Chat';

import './App.css';
import Assistants from './pages/Assistants';
import AddAssistant from './pages/AddAssistant'
import Benchmarking from './pages/Benchmarking';
import DataCollections from "./pages/DataCollections";
import EditCollection from "./pages/EditCollection";
import WidgetSessionsMessenger from "./pages/WidgetSessionsMessenger";
import Vault from "./pages/Vault";
import PlatformSettings from "./pages/PlatformSettings";
import ChannelsLayout from './layouts/ChannelsLayout';
import AssistantChannels from './pages/AssistantChannels';
import Bots from './pages/channels/Bots';
import AddBot from './pages/channels/AddBot';
import BotConnectTelegram from './pages/channels/BotConnectTelegram';
import BotSessionsMessenger from './pages/channels/BotSessionsMessenger';
import Widgets from './pages/channels/Widgets';
import ApiKeys from './pages/channels/ApiKeys';
import Prompts from './pages/Prompts';
import Connectors from './pages/Connectors';
import PromptDetail from './pages/PromptDetail';
import {
  IntegrationLegacyRedirect,
  IntegrationWidgetsLegacyRedirect,
  BotsLegacyRedirect,
  BotActivityLegacyRedirect,
} from './routes/LegacyRedirects';

const { Content } = Layout;
const { useBreakpoint } = Grid;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AuthGate({ children }) {
  const { keycloak, initialized } = useKeycloak();
  const { pathname } = useLocation();
  if (!initialized) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--color-bg-primary)' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!keycloak.authenticated && !pathname.startsWith('/s/')) {
    keycloak.login();
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--color-bg-primary)' }}>
        <Spin size="large" tip="Redirecting to login..." />
      </div>
    );
  }
  return children;
}

function AppShell() {
  const { chatOnly } = useShareChrome();
  const { initialized } = useKeycloak();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  if (!initialized) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--color-bg-primary)' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <NotificationProvider>
    <SearchProvider>
    <UserSessionProvider>
    <CollectionsDataProvider>
    <Router>
      <ScrollToTop />
      <AuthGate>
      <Layout style={{ minHeight: '100vh' }}>
          {!chatOnly && (
          <HeaderBar 
            collapsed={sidebarCollapsed} 
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            onMobileMenuClick={() => setMobileDrawerVisible(true)}
            isMobile={isMobile}
          />
          )}
          <Layout style={{ marginTop: chatOnly ? 0 : 'var(--header-height)' }}>
            {!chatOnly && !isMobile && <Sidebar collapsed={sidebarCollapsed} />}
            {!chatOnly && isMobile && (
              <Sidebar 
                collapsed={false}
                mobileDrawerVisible={mobileDrawerVisible}
                onMobileDrawerClose={() => setMobileDrawerVisible(false)}
              />
            )}
            <Layout className={`site-layout${chatOnly ? ' share-chat-only' : ''}${sidebarCollapsed && !isMobile ? ' sidebar-collapsed' : ''}`}>
          <Content>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard/overview" replace />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<DashboardOverview />} />
                <Route path="usage" element={<DashboardUsage />} />
                <Route path="reliability" element={<DashboardReliability />} />
                <Route path="response-quality" element={<DashboardResponseQuality />} />
                <Route path="spend" element={<DashboardSpend />} />
              </Route>
              <Route path="/rag-monitoring" element={<Navigate to="/dashboard/response-quality" replace />} />
              <Route path="/users" element={<UserManagement />} />
              <Route path="/users/create" element={<CreateUser />} />
              <Route path="/users/:userId/profile" element={<UserProfile />} />
              <Route path="/groups/create" element={<CreateGroup />} />
              <Route path="/collections" element={<DataCollections />} />
              <Route path="/collections/:collectionId/edit" element={<EditCollection />} />
              <Route path="/collections/:collectionId" element={<DataCollections />} />
              <Route path="/assistants" element={<Assistants />} />
              <Route path="/assistants/add" element={<AddAssistant />} />
              <Route path="/assistants/:assistantId/edit" element={<AddAssistant />} />
              <Route path="/assistants/:assistantId/channels" element={<AssistantChannels />} />
              <Route path="/prompts" element={<Prompts />} />
              <Route path="/connectors" element={<Connectors />} />
              <Route path="/prompts/:promptId" element={<PromptDetail />} />
              <Route path="/channels" element={<ChannelsLayout />}>
                <Route index element={<Navigate to="/channels/widgets" replace />} />
                <Route path="widgets" element={<Widgets />} />
                <Route path="api" element={<ApiKeys />} />
                <Route path="shares/:shareId" element={<WidgetSessionsMessenger />} />
                <Route path="shares/:shareId/:sessionId" element={<WidgetSessionsMessenger />} />
                <Route path="widgets/:widgetId/sessions" element={<WidgetSessionsMessenger />} />
                <Route path="widgets/:widgetId/sessions/:sessionId" element={<WidgetSessionsMessenger />} />
                <Route path="bots" element={<Bots />} />
                <Route path="bots/add" element={<AddBot />} />
                <Route path="bots/:botId/edit" element={<AddBot />} />
                <Route path="bots/:botId/connect" element={<BotConnectTelegram />} />
                <Route path="bots/:botId/activity" element={<BotSessionsMessenger />} />
                <Route path="bots/:botId/activity/:chatId" element={<BotSessionsMessenger />} />
                <Route path="bots/:botId/sessions" element={<BotActivityLegacyRedirect />} />
                <Route path="bots/:botId/sessions/:sessionId" element={<BotActivityLegacyRedirect />} />
              </Route>
              <Route path="/bots/*" element={<BotsLegacyRedirect />} />
              <Route path="/integration" element={<IntegrationLegacyRedirect />} />
              <Route path="/integration/widgets/*" element={<IntegrationWidgetsLegacyRedirect />} />
              <Route path="/chat/:assistantId" element={<Chat />} />
              <Route path="/s/:token" element={<Chat />} />
              <Route path="/benchmarking" element={<Benchmarking />} />
              <Route path="/vault" element={<Vault />} />
              <Route path="/platform-settings" element={<PlatformSettings />} />
              <Route path="*" element={<Navigate to="/dashboard/overview" replace />} />
            </Routes>
          </Content>
        </Layout>
          </Layout>
          <NotificationContainer />
      </Layout>
      </AuthGate>
    </Router>
    </CollectionsDataProvider>
    </UserSessionProvider>
    </SearchProvider>
    </NotificationProvider>
  );
}


function App() {
  return (
    <ShareChromeProvider>
      <AppShell />
    </ShareChromeProvider>
  );
}

export default App;
